import { Lead, PrivateUserInfoType, Vehicle } from '@otw/models';
import { LeadCustomerRecord } from '@otw/models/leads/lead';

export const normalizeText = (value?: string | null): string => (value || '').trim().toLowerCase();

export const formatCurrency = (value?: number | null): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(Number(value || 0));

export const formatNumber = (value?: number | null): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));

export const getVehicleSoldValue = (vehicle: Vehicle): number => {
  const financeTotal = Number(vehicle.finance?.total_sale_price);
  if (!Number.isNaN(financeTotal) && financeTotal > 0) return financeTotal;

  const base = Number(vehicle.optic_list_price) || 0;
  const warranty = Number(vehicle.warranty?.customer_cost) || 0;
  const shipping = Number(vehicle.shipping?.customer_shipping_cost) || 0;
  return base + warranty + shipping;
};

export const getProjectedMileage = (vehicle: Vehicle): number | null => {
  if (!vehicle.sold_date || typeof vehicle.miles !== 'number') return null;
  const soldAt = new Date(vehicle.sold_date).getTime();
  if (Number.isNaN(soldAt)) return vehicle.miles;
  const yearsElapsed = (Date.now() - soldAt) / (1000 * 60 * 60 * 24 * 365);
  return Math.round(vehicle.miles + yearsElapsed * 25000);
};

export const getDealQualityLabel = (dealQuality: Vehicle['dealQuality']): 'Good' | 'Bad' | 'Unknown' => {
  if (!dealQuality) return 'Unknown';
  if (dealQuality === 'great' || dealQuality === 'good' || dealQuality === 'average') return 'Good';
  return 'Bad';
};

export const findMatchingClientUser = (
  lead: Lead,
  clientUsers: PrivateUserInfoType[],
): PrivateUserInfoType | undefined => {
  const clientId = lead.client_id?.trim();
  if (clientId) {
    const byId = clientUsers.find((user) => user.id === clientId);
    if (byId) return byId;
  }

  const leadEmail = normalizeText(lead.email);
  const leadPhone = normalizeText(lead.phone);
  const leadName = normalizeText(lead.customerName);

  return clientUsers.find((user) => {
    const userEmail = normalizeText(user.email);
    const userPhone = normalizeText(user.phone);
    const userName = normalizeText(`${user.first_name || ''} ${user.last_name || ''}`);

    if (leadEmail && userEmail && leadEmail === userEmail) return true;
    if (leadPhone && userPhone && leadPhone === userPhone) return true;
    if (leadName && userName && leadName === userName) return true;
    return false;
  });
};

export const buildCustomerRecords = (
  leads: Lead[],
  soldVehicles: Vehicle[],
  users: PrivateUserInfoType[],
): LeadCustomerRecord[] => {
  const clientUsers = users.filter((user) => user.role === 'client');
  const records = new Map<string, LeadCustomerRecord>();

  leads.forEach((lead) => {
    const key = normalizeText(lead.email) || normalizeText(lead.phone) || normalizeText(lead.customerName) || lead.id;
    if (!records.has(key)) {
      const matchedUser = findMatchingClientUser(lead, clientUsers);
      records.set(key, {
        id: key,
        displayName: lead.customerName || 'Unknown customer',
        email: lead.email || undefined,
        phone: lead.phone || undefined,
        userId: matchedUser?.id,
        leads: [],
        leadCompanies: [],
        soldVehicles: [],
      });
    }
    const current = records.get(key)!;
    current.leads.push(lead);
    if (lead.company && !current.leadCompanies.includes(lead.company)) {
      current.leadCompanies.push(lead.company);
    }
  });

  records.forEach((customer) => {
    const sold = soldVehicles.filter((vehicle) => {
      const soldTo = vehicle.sold_to_user;
      if (!soldTo) return false;
      if (customer.userId && soldTo.id === customer.userId) return true;
      return normalizeText(soldTo.name) === normalizeText(customer.displayName);
    });
    customer.soldVehicles = sold;
  });

  return [...records.values()].sort((a, b) => b.soldVehicles.length - a.soldVehicles.length);
};
